/**
 * Shrink an oversized phone photo of a pathology report before it is uploaded.
 *
 * Blood-test images routinely exceed the 5 MB the backend accepts
 * (`BloodTestAnalysisService::MAX_DOCUMENT_BYTES`, and xoe-agent's
 * `generation_routes._MAX_DOCUMENT_BYTES`), and today the browser lets a 10 MB
 * photo through so the practitioner only learns it failed after a queue
 * round-trip. Only JPEG and PNG are touched: a PDF is never re-encoded.
 */

export const DOWNSCALE_THRESHOLD_BYTES = 4 * 1024 * 1024;

// Anthropic downsamples anything over 1568px on the long edge, so 2400 costs no accuracy.
export const DOWNSCALE_MAX_EDGE = 2400;

const DOWNSCALE_QUALITY = 0.85;

const DOWNSCALEABLE_TYPES = ['image/jpeg', 'image/png'];

const toJpegName = (name: string): string => {
	const base = name.replace(/\.[^.]+$/, '');
	return `${base || name}.jpg`;
};

export async function downscaleImage(file: File): Promise<File> {
	if (!DOWNSCALEABLE_TYPES.includes(file.type) || file.size <= DOWNSCALE_THRESHOLD_BYTES) {
		return file;
	}

	try {
		const bitmap = await createImageBitmap(file);
		const scale = Math.min(1, DOWNSCALE_MAX_EDGE / Math.max(bitmap.width, bitmap.height));

		const canvas = document.createElement('canvas');
		canvas.width = Math.round(bitmap.width * scale);
		canvas.height = Math.round(bitmap.height * scale);

		const context = canvas.getContext('2d');

		if (!context) {
			return file;
		}

		context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
		bitmap.close?.();

		const blob = await new Promise<Blob | null>((resolve) => {
			canvas.toBlob(resolve, 'image/jpeg', DOWNSCALE_QUALITY);
		});

		if (!blob || blob.size >= file.size) {
			return file;
		}

		return new File([blob], toJpegName(file.name), {
			type: 'image/jpeg',
			lastModified: file.lastModified,
		});
	} catch {
		// A canvas failure must never block an upload; the caller's own size check still applies.
		return file;
	}
}
